import type { TransportIngredientFilters } from '@apps/main/modules/recipe/ingredients/transportIngredient';
import {
  DEFAULT_AVAILABILITY_ORDER_SUGGESTION,
  TransportIngredient,
} from '@apps/main/modules/recipe/ingredients/transportIngredient';

import { assertValue } from '@libs/utils';
import {
  AvailabilityOrder,
  IngredientType,
} from '@libs/trip-planner-interfaces';
import {
  trackDeserializeIngredientWithEncodedPlusSign,
  trackDeserializeTransportWithCoordinates,
  trackDeserializeTransportWithoutSuggestionOrderPrefix,
} from '@libs/feature-toggle';

import {
  TRANSPORT_PROPS_SEPARATOR,
  TRANSPORT_FILTERS_SEPARATOR,
  TRANSPORT_PORT_WRAPPER_OPEN,
  TRANSPORT_PORT_WRAPPER_CLOSE,
  TRANSPORT_PORT_SEPARATOR,
  TRANSPORT_ID_SEPARATOR,
  TRANSPORT_CARRIER_SEPARATOR,
  SUGGESTION_ORDER_PREFIX,
  SerializedAvailabilityOrder,
} from './constants';
import {
  safelyDeserializeFilter,
  rangeTimeSerializer,
  rangePriceSerializer,
} from './helpers';

export interface SerializedIngredientParameters {
  serializedPorts: string;
  transportIds: string[] | undefined;
  serializedProps: string[];
  serializedAvailabilityOrder: string;
}

export function deserializeIngredientParameters(
  value: string
): SerializedIngredientParameters {
  if (value.includes(encodeURIComponent('+'))) {
    trackDeserializeIngredientWithEncodedPlusSign(IngredientType.Transport);
  }

  const serializedIngredient = decodeURIComponent(value).replace(/\+/g, ' ');

  const propsIndex = serializedIngredient.indexOf(TRANSPORT_PROPS_SEPARATOR);

  const serializedPorts = serializedIngredient.slice(0, propsIndex);

  const fullProps = serializedIngredient
    .slice(propsIndex + 1)
    .split(TRANSPORT_FILTERS_SEPARATOR);

  const transportIds = deserializeTransportIds(fullProps[0]);

  const serializedAvailabilityOrder =
    fullProps.find((prop) => prop.startsWith(SUGGESTION_ORDER_PREFIX)) ?? '';

  let serializedProps = fullProps.slice(1);

  if (serializedAvailabilityOrder) {
    serializedProps = serializedProps.filter(
      (prop) => prop !== serializedAvailabilityOrder
    );
  } else {
    trackDeserializeTransportWithoutSuggestionOrderPrefix();
  }

  return {
    serializedPorts,
    transportIds,
    serializedProps,
    serializedAvailabilityOrder,
  };
}

export function deserializePorts(serializedPorts: string): {
  fromPort: string;
  toPort: string;
} {
  const hasCoordinates = serializedPorts.includes(TRANSPORT_PORT_WRAPPER_OPEN);

  if (hasCoordinates) {
    trackDeserializeTransportWithCoordinates();
  }

  const portSeparatorIndex = hasCoordinates
    ? serializedPorts.indexOf(
        `${TRANSPORT_PORT_WRAPPER_CLOSE}${TRANSPORT_PORT_SEPARATOR}`
      ) + 1
    : serializedPorts.indexOf(TRANSPORT_PORT_SEPARATOR);

  const serializedFromPort = serializedPorts.slice(0, portSeparatorIndex);
  const serializedToPort = serializedPorts.slice(portSeparatorIndex + 1);

  const fromPort = serializedFromPort.split(TRANSPORT_PORT_WRAPPER_OPEN)[0];
  const toPort = serializedToPort.split(TRANSPORT_PORT_WRAPPER_OPEN)[0];

  assertValue(fromPort, 'transportSerializer.ts > deserializePorts > from');
  assertValue(toPort, 'transportSerializer.ts > deserializePorts > to');

  return {
    fromPort,
    toPort,
  };
}

function deserializeTransportIds(
  serializedTransportIds?: string
): string[] | undefined {
  return safelyDeserializeFilter(serializedTransportIds, (value) =>
    value.split(TRANSPORT_ID_SEPARATOR)
  );
}

export function deserializeTransportFilters(
  serializedTransportFilters: string[]
): TransportIngredientFilters {
  const filters = TransportIngredient.defaultFilters;

  filters.departureTimeFilters = safelyDeserializeFilter(
    serializedTransportFilters[0],
    rangeTimeSerializer.deserialize
  );
  filters.arrivalTimeFilters = safelyDeserializeFilter(
    serializedTransportFilters[1],
    rangeTimeSerializer.deserialize
  );
  filters.maxDurationInHours = safelyDeserializeFilter(
    serializedTransportFilters[2],
    (value) => Number(value)
  );
  filters.priceFilters = safelyDeserializeFilter(
    serializedTransportFilters[3],
    rangePriceSerializer.deserialize
  );
  filters.carrierFilters =
    safelyDeserializeFilter(serializedTransportFilters[4], (value) =>
      value.split(TRANSPORT_CARRIER_SEPARATOR)
    ) ?? [];

  return filters;
}

export function deserializeAvailabilityOrder(value: string): AvailabilityOrder {
  const serializedAvailabilityOrder = value.replace(
    SUGGESTION_ORDER_PREFIX,
    ''
  );

  switch (serializedAvailabilityOrder) {
    case SerializedAvailabilityOrder.None:
      return AvailabilityOrder.None;

    case SerializedAvailabilityOrder.Cheapest:
      return AvailabilityOrder.Cheapest;

    case SerializedAvailabilityOrder.Popular:
      return AvailabilityOrder.Popular;

    default:
      return DEFAULT_AVAILABILITY_ORDER_SUGGESTION;
  }
}
